import React from "react";
import { Input, Button, Form, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";

/* ── column search dropdown ──────────────────────────────────────────────── */
export function colSearch(dataIndex, title, themeColor = "#6366f1") {
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 14, width: 230 }}>
        <Input
          autoFocus
          placeholder={`Search ${title}…`}
          value={selectedKeys[0]}
          prefix={<SearchOutlined style={{ color: "#c4c7cd" }} />}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 10, borderRadius: 7, height: 36 }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            size="small"
            onClick={() => confirm()}
            style={{
              flex: 1, borderRadius: 6, height: 30, fontWeight: 600,
              background: themeColor, borderColor: themeColor, color: "#fff",
            }}
          >
            Apply
          </Button>
          <Button
            size="small"
            onClick={() => { clearFilters?.(); confirm(); }}
            style={{ flex: 1, borderRadius: 6, height: 30 }}
          >
            Reset
          </Button>
        </div>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? themeColor : "#9ca3af", fontSize: 13 }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
  };
}

/* ── form field with uppercase label ────────────────────────────────────── */
export function FormField({ name, label, rules, valuePropName, children }) {
  return (
    <Form.Item
      name={name}
      rules={rules}
      valuePropName={valuePropName}
      style={{ marginBottom: 20 }}
      label={
        <span style={{
          fontSize: 11, fontWeight: 700, color: "#374151",
          textTransform: "uppercase", letterSpacing: "0.07em",
        }}>
          {label}
        </span>
      }
    >
      {children}
    </Form.Item>
  );
}

/* ── active / inactive tag ───────────────────────────────────────────────── */
export function StatusTag({ active }) {
  return (
    <Tag
      color={active ? "success" : "error"}
      style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, padding: "1px 10px", margin: 0 }}
    >
      {active ? "Active" : "Inactive"}
    </Tag>
  );
}

/* ── small icon action button ────────────────────────────────────────────── */
export function actionBtnStyle(color) {
  return {
    width: 30, height: 30, borderRadius: 8,
    border: "1px solid #e5e7eb", background: "#f9fafb",
    cursor: "pointer", color,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s", padding: 0,
  };
}

/* ── native CSV export ───────────────────────────────────────────────────── */
export function exportCSV(rows, filename) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csvLines = [
    headers.join(","),
    ...rows.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
  ];
  const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* ── Excel export (SheetJS) ─────────────────────────────────────────────── */
export async function exportExcel(rows, filename) {
  if (!rows?.length) return;
  const XLSX = await import("xlsx");
  const ws   = XLSX.utils.json_to_sheet(rows);
  const wb   = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  /* auto column widths */
  const headers = Object.keys(rows[0]);
  ws["!cols"] = headers.map(h => ({
    wch: Math.min(40, Math.max(h.length + 2, ...rows.map(r => String(r[h] ?? "").length))),
  }));

  XLSX.writeFile(wb, filename);
}
